import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { PageHeader, EmptyState, StatusBadge } from "../components/common";
import AskMariannePanel from "../components/AskMariannePanel";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import RichEditor from "../components/RichEditor";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from "../components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import { Plus, Handshake, Trash2, FileDown, PenLine, Share2, Copy, Check } from "lucide-react";
import { toast } from "sonner";
import SignaturePad from "../components/SignaturePad";

const API_BASE = process.env.REACT_APP_BACKEND_URL;

const types = ["General", "NDA", "Service", "Employment", "Lease", "Partnership", "Sales", "Consulting"];
const statusOptions = ["Draft", "Out for Signature", "Partially Signed", "Awaiting Owner", "Signed", "Expired"];

export default function AgreementsPage() {
  const [items, setItems] = useState([]);
  const [docIntelAg, setDocIntelAg] = useState("");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ title: "", agreement_type: "NDA", counterparty: "", status: "Draft", content: "" });
  const [editing, setEditing] = useState(null);

  const load = () => api.get("/agreements").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!form.title) return toast.error("Title required");
    if (editing) {
      await api.patch(`/agreements/${editing}`, form);
      toast.success("Updated");
    } else {
      await api.post("/agreements", form);
      toast.success("Agreement created");
    }
    setOpen(false);
    setEditing(null);
    setForm({ title: "", agreement_type: "NDA", counterparty: "", status: "Draft", content: "" });
    load();
  };

  const remove = async (id) => {
    if (!window.confirm("Delete agreement?")) return;
    await api.delete(`/agreements/${id}`);
    load();
  };

  const exportPdf = (a) => {
    const token = localStorage.getItem("marianne_token");
    fetch(`${API_BASE}/api/agreements/${a.id}/pdf`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => r.blob())
      .then((blob) => {
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = `${a.title || "agreement"}.pdf`;
        link.click();
        URL.revokeObjectURL(url);
      });
  };

  // Signing state
  const [signing, setSigning] = useState(null); // agreement object
  const [sigName, setSigName] = useState("");
  const [sigImage, setSigImage] = useState("");
  const [shareUrl, setShareUrl] = useState("");
  const [copied, setCopied] = useState(false);

  const openSignDialog = (a) => {
    setSigning(a);
    setSigName("");
    setSigImage("");
    setShareUrl("");
    setCopied(false);
  };

  const submitOwnerSignature = async () => {
    if (!sigImage) return toast.error("Please add your signature");
    try {
      const { data } = await api.post(`/agreements/${signing.id}/sign`, {
        image_base64: sigImage,
        full_name: sigName,
      });
      toast.success("Signed.");
      setSigning((prev) => prev ? { ...prev, status: data.status, signatures: data.signatures } : prev);
      setSigImage("");
      setSigName("");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Signing failed");
    }
  };

  const generateShareLink = async () => {
    try {
      const { data } = await api.post(`/agreements/${signing.id}/share`);
      const url = `${window.location.origin}/sign/${data.token}`;
      setShareUrl(url);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not create link");
    }
  };

  const revokeShareLink = async () => {
    try {
      await api.delete(`/agreements/${signing.id}/share`);
      setShareUrl("");
      toast.success("Link revoked.");
      load();
    } catch (err) {
      toast.error("Revoke failed");
    }
  };

  const copyShare = async () => {
    if (!shareUrl) return;
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch {
      toast.error("Copy failed");
    }
  };

  const edit = (a) => {
    setEditing(a.id);
    setForm({
      title: a.title, agreement_type: a.agreement_type, counterparty: a.counterparty,
      status: a.status, content: a.content,
    });
    setOpen(true);
  };

  const tone = (s) => ({ Draft: "neutral", "Out for Signature": "warn", "Partially Signed": "warn", "Awaiting Owner": "warn", Signed: "success", Expired: "danger" }[s] || "neutral");

  return (
    <div data-testid="agreements-page">
      <PageHeader
        eyebrow="Les Contrats"
        title="Agreements"
        description="Contracts, NDAs, leases — drafted, tracked, and signed with clarity."
        action={
          <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) { setEditing(null); setForm({ title: "", agreement_type: "NDA", counterparty: "", status: "Draft", content: "" }); } }}>
            <DialogTrigger asChild>
              <Button className="rounded-none bg-[#2A0F5A] hover:bg-[#3D1F85] text-[#FAFAF8]" data-testid="agreements-new-btn">
                <Plus size={14} className="mr-1.5" /> New agreement
              </Button>
            </DialogTrigger>
            <DialogContent className="rounded-none max-w-xl">
              <DialogHeader><DialogTitle className="font-serif text-2xl">{editing ? "Edit agreement" : "New agreement"}</DialogTitle></DialogHeader>
              <div className="space-y-4">
                <div>
                  <Label className="label-eyebrow mb-2 block">Title</Label>
                  <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="rounded-none border-[#E8E3F0]" data-testid="agreement-title-input" />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="label-eyebrow mb-2 block">Type</Label>
                    <Select value={form.agreement_type} onValueChange={(v) => setForm({ ...form, agreement_type: v })}>
                      <SelectTrigger className="rounded-none border-[#E8E3F0]" data-testid="agreement-type-select"><SelectValue /></SelectTrigger>
                      <SelectContent>{types.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="label-eyebrow mb-2 block">Status</Label>
                    <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                      <SelectTrigger className="rounded-none border-[#E8E3F0]" data-testid="agreement-status-select"><SelectValue /></SelectTrigger>
                      <SelectContent>{statusOptions.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                </div>
                <div>
                  <Label className="label-eyebrow mb-2 block">Counterparty</Label>
                  <Input value={form.counterparty} onChange={(e) => setForm({ ...form, counterparty: e.target.value })} className="rounded-none border-[#E8E3F0]" data-testid="agreement-counterparty-input" />
                </div>
                <div>
                  <Label className="label-eyebrow mb-2 block">Content</Label>
                  <RichEditor value={form.content} onChange={(v) => setForm({ ...form, content: v })} placeholder="Paste or draft the agreement body…" minHeight="260px" testId="agreement-content-editor" />
                </div>
              </div>
              <DialogFooter>
                <Button className="rounded-none bg-[#2A0F5A] hover:bg-[#3D1F85] text-[#FAFAF8]" onClick={save} data-testid="agreement-save-btn">{editing ? "Update" : "Create"}</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        }
      />
      {items.length === 0 ? (
        <EmptyState
          icon={Handshake}
          title="No agreements yet"
          description="Create a contract — NDAs, service agreements, leases — track status through signature."
        />
      ) : (
        <div className="border border-[#E8E3F0] bg-white">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-[#E8E3F0]">
                <th className="text-left px-4 py-3 label-eyebrow">Title</th>
                <th className="text-left px-4 py-3 label-eyebrow hidden md:table-cell">Type</th>
                <th className="text-left px-4 py-3 label-eyebrow hidden md:table-cell">Counterparty</th>
                <th className="text-left px-4 py-3 label-eyebrow">Status</th>
                <th className="text-right px-4 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {items.map((a) => (
                <tr key={a.id} className="border-b border-[#E8E3F0] hover:bg-[#FAFAF8]" data-testid={`agreement-row-${a.id}`}>
                  <td className="px-4 py-3"><button onClick={() => edit(a)} className="font-serif text-lg text-left">{a.title}</button></td>
                  <td className="px-4 py-3 hidden md:table-cell">{a.agreement_type}</td>
                  <td className="px-4 py-3 hidden md:table-cell text-[#4A5568]">{a.counterparty || "—"}</td>
                  <td className="px-4 py-3"><StatusBadge tone={tone(a.status)}>{a.status}</StatusBadge></td>
                  <td className="px-4 py-3 text-right space-x-3" onClick={(e) => e.stopPropagation()}>
                    <button onClick={() => openSignDialog(a)} className="text-[#4A5568] hover:text-[#7B56C9]" title="Sign & Send" data-testid={`agreement-sign-${a.id}`}><PenLine size={14} /></button>
                    <button onClick={() => exportPdf(a)} className="text-[#4A5568] hover:text-[#2A0F5A]" title="Export PDF" data-testid={`agreement-pdf-${a.id}`}><FileDown size={14} /></button>
                    <button onClick={() => remove(a.id)} className="text-[#4A5568] hover:text-red-700" data-testid={`agreement-del-${a.id}`}><Trash2 size={14} /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Ask Marianne — Document Intelligence */}
      {items.length > 0 && (
        <div className="mt-8 border border-[#E8E3F0] bg-white" data-testid="doc-intel-block">
          <div className="px-5 py-4 border-b border-[#E8E3F0]">
            <div className="label-eyebrow text-[#C5A059] mb-1">Document intelligence</div>
            <div className="font-serif text-2xl leading-tight">Ask Marianne about any agreement</div>
            <div className="text-xs text-[#4A5568] mt-1">Answers are grounded in the actual clause text. Marianne says so when the answer isn't in the document.</div>
          </div>
          <select
            className="w-full border-b border-[#E8E3F0] bg-white px-5 py-3 text-sm"
            onChange={(e) => setDocIntelAg(e.target.value)}
            value={docIntelAg || ""}
            data-testid="doc-intel-agreement-select"
          >
            <option value="">Pick an agreement to analyse…</option>
            {items.map((a) => (<option key={a.id} value={a.id}>{a.title}</option>))}
          </select>
          {docIntelAg && (
            <AskMariannePanel agreementId={docIntelAg} agreementTitle={items.find((a) => a.id === docIntelAg)?.title} alwaysOpen />
          )}
        </div>
      )}

      {/* Sign & Send dialog */}
      <Dialog open={!!signing} onOpenChange={(v) => !v && setSigning(null)}>
        <DialogContent className="rounded-none max-w-2xl" data-testid="sign-dialog">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl">Sign &amp; Send</DialogTitle>
          </DialogHeader>
          {signing && (
            <div className="space-y-6">
              <div>
                <div className="label-eyebrow mb-1">Document</div>
                <div className="font-serif text-xl">{signing.title}</div>
                <div className="text-xs text-[#4A5568] mt-1">{signing.agreement_type} · Status: {signing.status}</div>
              </div>

              {/* Existing signatures */}
              {(signing.signatures && signing.signatures.length > 0) && (
                <div className="border border-[#E8E3F0] bg-[#FAFAF8] p-4">
                  <div className="label-eyebrow mb-2">Already signed by</div>
                  <ul className="space-y-1 text-sm">
                    {signing.signatures.map((s, i) => (
                      <li key={i} className="flex items-center justify-between">
                        <span><strong>{s.full_name || "—"}</strong> · {s.role}</span>
                        <span className="text-xs text-[#4A5568]">{(s.signed_at || "").slice(0, 19).replace("T", " ")}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Owner signature pad */}
              <div className="border border-[#E8E3F0] bg-white p-4">
                <div className="label-eyebrow mb-3">Sign as owner</div>
                <SignaturePad
                  value={sigImage}
                  onChange={setSigImage}
                  name={sigName}
                  onNameChange={setSigName}
                  testId="owner-signature-pad"
                />
                <Button onClick={submitOwnerSignature} className="mt-3 rounded-none bg-[#2A0F5A] hover:bg-[#3D1F85] text-[#FAFAF8]" data-testid="owner-sign-btn">
                  <PenLine size={14} className="mr-1.5" /> Apply my signature
                </Button>
              </div>

              {/* Share link */}
              <div className="border border-[#E8E3F0] bg-white p-4">
                <div className="flex items-center justify-between mb-3">
                  <div className="label-eyebrow">Send to counterparty</div>
                  {signing.share_token && !signing.share_revoked && (
                    <button onClick={revokeShareLink} className="text-xs text-red-700 hover:underline" data-testid="revoke-share-btn">Revoke link</button>
                  )}
                </div>

                {!shareUrl && !signing.share_token ? (
                  <Button variant="outline" className="rounded-none border-[#E8E3F0]" onClick={generateShareLink} data-testid="generate-share-btn">
                    <Share2 size={14} className="mr-1.5" /> Generate signing link
                  </Button>
                ) : (
                  <div>
                    <div className="flex gap-2">
                      <Input
                        readOnly
                        value={shareUrl || `${window.location.origin}/sign/${signing.share_token}`}
                        className="rounded-none border-[#E8E3F0] font-mono text-xs"
                        data-testid="share-url-input"
                      />
                      <Button onClick={copyShare} className="rounded-none bg-[#7B56C9] hover:bg-[#6844BA] text-white shrink-0" data-testid="copy-share-btn">
                        {copied ? <Check size={14} /> : <Copy size={14} />}
                      </Button>
                    </div>
                    <p className="text-xs text-[#4A5568] mt-2 leading-relaxed">
                      Send this link by email or message. The recipient will be able to view the document and sign it once. Their signature, name, IP, and timestamp will be recorded.
                    </p>
                  </div>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
